import React, { useMemo, useCallback } from 'react';
import { useIntl } from "umi";
import { roundingWithoutZero } from '@home/utils/dbDataFormat/dataFormatUtils';
import { Table, TableColumn, Text, notification } from "tea-component";
import { autotip, scrollable } from 'tea-component/lib/table/addons';
import TagFilterSelect from "@/pages/datalab/components/TagFilterSelect";
import BubbleEllipsis from "@/pages/datalab/components/BubbleEllipsis";
import DropdownSelect from "@/pages/datalab/components/DropdownSelect";
import ColorDropdown from "@/pages/datalab/components/ColorDropdown";
import type { TDropdownValue } from '@/pages/datalab/components/DropdownSelect/types';
import {
  IHorizontalBarChange,
  IHorizontalBarProps,
  THorizontalBarRecord,
} from './type';
import { IBarPercentageItem } from '../type';
import {
  parseDropdownValue,
  getDropdownValueFromNumber,
  CUSTOM_MIN_DEFAULT,
  CUSTOM_MAX_DEFAULT,
} from './utils';
import './index.scss';

const MIN_MAX_DISPLAY_DECIMALS = 6;

/** 生成 min / max 两列配置，共用「自动」选项与解析逻辑；校验失败时不关闭下拉并展示 notification + 输入框红框 */
function getMinMaxColumns(
  formatMessage: (opts: { id: string; }) => string,
  records: THorizontalBarRecord[],
  onChange: (value: Partial<IHorizontalBarChange>) => void,
  onValidationError: (message: string) => void
): TableColumn[] {
  const autoOption = {
    value: 'auto' as const,
    label: formatMessage({ id: 'dbl_table_condition_auto' }),
  };
  const customInputConfigMin = {
    enabled: true,
    label: formatMessage({ id: 'dbl_table_condition_custom_value' }),
    placeholder: formatMessage({ id: 'dbl_table_condition_custom_value_placeholder' }),
    defaultCustomValue: CUSTOM_MIN_DEFAULT,
  };
  const customInputConfigMax = {
    enabled: true,
    label: formatMessage({ id: 'dbl_table_condition_custom_value' }),
    placeholder: formatMessage({ id: 'dbl_table_condition_custom_value_placeholder' }),
    defaultCustomValue: CUSTOM_MAX_DEFAULT,
  };
  const listWidth = 300;

  const makeOnChange = (id: string, field: 'min' | 'max') => (v: TDropdownValue) => {
    const val: number | null = parseDropdownValue(v);
    const list = records?.map((item: THorizontalBarRecord) =>
      item?.id === id ? { ...item, [field]: val } : item
    );
    onChange({ table_bar_percentage: list });
  };

  const makeValidate = (id: string, field: 'min' | 'max') => (v: TDropdownValue) => {
    const val: number | null = parseDropdownValue(v);
    const record = records?.find((item: THorizontalBarRecord) => item?.id === id);
    if (!record) return undefined;
    const currentMin = record.min;
    const currentMax = record.max;
    if (field === 'min' && val !== null && currentMax !== null && currentMax !== undefined && val > currentMax) {
      return formatMessage({ id: 'dbl_table_condition_max_less_than_min' });
    }
    if (field === 'max' && val !== null && currentMin !== null && currentMin !== undefined && val < currentMin) {
      return formatMessage({ id: 'dbl_table_condition_max_less_than_min' });
    }
    return undefined;
  };

  return [
    {
      key: 'min',
      header: formatMessage({ id: 'dbl_table_condition_min' }),
      width: 180,
      render: (record: THorizontalBarRecord) => {
        const { min, id } = record || {};
        return (
          <DropdownSelect
            options={[autoOption]}
            customInput={{
              ...customInputConfigMin,
              defaultCustomValue: record?.customMin ?? CUSTOM_MIN_DEFAULT,
            }}
            value={getDropdownValueFromNumber(min)}
            formatCustomDisplay={(value: number) =>
              String(roundingWithoutZero(value, MIN_MAX_DISPLAY_DECIMALS))
            }
            listWidth={listWidth}
            onChange={makeOnChange(id, 'min')}
            validate={makeValidate(id, 'min')}
            onValidationError={onValidationError}
          />
        );
      },
    },
    {
      key: 'max',
      header: formatMessage({ id: 'dbl_table_condition_max' }),
      width: 180,
      render: (record: THorizontalBarRecord) => {
        const { max, id } = record || {};
        return (
          <DropdownSelect
            options={[autoOption]}
            customInput={{
              ...customInputConfigMax,
              defaultCustomValue: record?.customMax ?? CUSTOM_MAX_DEFAULT,
            }}
            value={getDropdownValueFromNumber(max)}
            formatCustomDisplay={(value: number) =>
              String(roundingWithoutZero(value, MIN_MAX_DISPLAY_DECIMALS))
            }
            listWidth={listWidth}
            onChange={makeOnChange(id, 'max')}
            validate={makeValidate(id, 'max')}
            onValidationError={onValidationError}
          />
        );
      },
    },
  ];
}

export const HorizontalBar: React.FC<IHorizontalBarProps> = props => {
  const { value, onChange, metrics, table } = props || {};
  const { formatMessage } = useIntl();

  const { metricsValue, records } = useMemo(() => {
    const metricsValue = value?.map((item: { id: string; }) => item?.id) || [];
    const records: THorizontalBarRecord[] = (value || []) as THorizontalBarRecord[];
    return { metricsValue, records };
  }, [value]);

  const options = useMemo(() => (metrics || [])?.map((item: any) => ({
    ...item,
    value: item.id,
    text: item?.column_display
  })), [metrics]);

  const newRecords = useMemo(() => {
    const filterList = records?.filter((item: THorizontalBarRecord) => options?.some((v: any) => v?.id === item?.id));
    const barPercentageMinMax = table?.barPercentageMinMax || [];
    return filterList?.map((item: THorizontalBarRecord) => {
      const selectItem = options?.find((v: any) => v?.id === item?.id);
      const barPercentageMinMaxItem = barPercentageMinMax?.find((v: any) => (v?.table_code === item?.table_code && v?.metric === item?.metric));
      return {
        ...item,
        name: selectItem?.text ? selectItem?.text : item?.metric,
        customMin: item?.min ?? barPercentageMinMaxItem?.min ?? undefined,
        customMax: item?.max ?? barPercentageMinMaxItem?.max ?? undefined,
      };
    });
  }, [options, table, records]);

  const onValidationError = useCallback((message: string) => {
    notification.error({ description: message });
  }, []);

  const minMaxColumns = useMemo(
    () => getMinMaxColumns(formatMessage, newRecords, onChange, onValidationError),
    [formatMessage, newRecords, onChange, onValidationError]
  );

  const columns: TableColumn[] = [
    {
      key: "name",
      header: formatMessage({ id: 'dbl_table_condition_merrics' }),
      width: 200,
      fixed: 'left',
      render: (record: THorizontalBarRecord) => {
        const columnName = record?.name;
        return columnName ? (
          <BubbleEllipsis content={columnName} className={'data-management-table-desc-bubble'}>
            <Text style={{ width: '100%' }} overflow>
              {columnName}
            </Text>
          </BubbleEllipsis>
        ) : '-';
      },
    },
    {
      key: "colorTheme",
      header: formatMessage({ id: 'dbl_table_condition_color_theme' }),
      width: 280,
      render: (record: THorizontalBarRecord) => {
        const { colorTheme, id } = record || {};
        return <ColorDropdown
          colorTheme={colorTheme as any}
          onChange={(value: any) => {
            const list = records?.map((item: THorizontalBarRecord) => {
              if (item?.id === id) {
                return {
                  ...item,
                  colorTheme: value,
                };
              }
              return item;
            });
            onChange({ table_bar_percentage: list });
          }} />;
      }
    },
    ...minMaxColumns,
    {
      key: "action",
      header: formatMessage({ id: 'dbl_table_condition_action' }),
      width: 100,
      render: (record: { id: string; }) => <div className="dbl-table-horizontal-bar-action">
        <span className="dbl-table-horizontal-bar-action-delete" onClick={() => {
          const list = records?.filter((item: THorizontalBarRecord) => item?.id !== record?.id);
          onChange({ table_bar_percentage: list });
        }}>
          {formatMessage({ id: 'dbl_table_condition_delete' })}
        </span>
      </div>
    }
  ];

  return <div className="dbl-table-horizontal-bar">
    <div className="dbl-table-horizontal-bar-title">
      <div className="dbl-table-horizontal-bar-title-label">{formatMessage({ id: 'dbl_table_condition_select_metrics' })}</div>
      <div className="dbl-table-horizontal-bar-title-desc">
        <TagFilterSelect
          value={metricsValue}
          options={(options || []) as any}
          listWidth={400}
          overlayClassName={`dbl-model-tag-select`}
          placeholder={formatMessage({ id: 'dbl_table_condition_select_metrics_placeholder' })}
          onChange={(value: string[]) => {
            const list = (value || [])?.map((id): IBarPercentageItem => {
              const found = (records || [])?.find((rec: THorizontalBarRecord) => rec?.id === id);
              if (found) {
                const itemObj: IBarPercentageItem = {
                  id: found.id,
                  table_code: found.table_code,
                  metric: found.metric,
                  colorTheme: found.colorTheme,
                  min: found.min as number | null,
                  max: found.max as number | null,
                };
                return itemObj;
              }
              const selectItem = (options || [])?.find((opt: any) => opt?.id === id);
              const obj: IBarPercentageItem = {
                id,
                table_code: selectItem?.table_code || "t0",
                metric: selectItem?.column_name,
                colorTheme: {
                  type: 'solidColor',
                  color: [{ hexa: "#E0EAFD", alpha: "100" }],
                },
                min: null,
                max: null,
              };
              return obj;
            });
            onChange({ table_bar_percentage: list });
          }}
          name={formatMessage({ id: 'dbl_table_metric' })}
          tooltip={undefined}
        />
      </div>
    </div>
    <div className="dbl-table-horizontal-bar-content">
      <Table
        columns={columns}
        records={newRecords}
        addons={[
          scrollable({
            maxHeight: 300,
            minWidth: 1000,
            scrollToTopOnChange: true,
          }),
          autotip({
            isLoading: false,
            isError: false,
            style: { lineHeight: '200px' },
            emptyText: formatMessage({ id: 'dbl_table_condition_select_metrics_placeholder' }),
          }),
        ]}
      />
    </div>
  </div>;
};